import React from 'react';
import { Button } from '@oysterjs/ui/button';
import { Description, ErrorMessage, Field, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';

import {
  EmailAmsLogDestination,
  IntegrationType,
  type IntegrationDetailFieldsFragment,
  type IntegrationFieldsFragment
} from '~/api/graphql';
import { useAmsLoggingEnabled } from '~/components/use-ams-logging-enabled';
import { SentEmailLoggingSwitch } from './sent-email-logging-switch';
import { useEmailLogPreference } from './use-email-log-preference';

const destinationLabels: Record<EmailAmsLogDestination, string> = {
  [EmailAmsLogDestination.Attachment]: 'Attachment',
  [EmailAmsLogDestination.Activity]: 'Activity',
  [EmailAmsLogDestination.Note]: 'Note',
  [EmailAmsLogDestination.Suspense]: 'Suspense'
};

type SettingsProps = {
  integration: IntegrationDetailFieldsFragment;
  emailLoggingIntegration: IntegrationFieldsFragment | undefined;
  canUpdate: boolean;
};

export function SentEmailLoggingSettings(props: SettingsProps) {
  const [isSaving, setIsSaving] = React.useState(false);
  const enabled = useAmsLoggingEnabled();
  if (!enabled) return null;
  return (
    <div className="space-y-4">
      <SentEmailLoggingSwitch {...props} disabled={isSaving} onSavingChange={setIsSaving} />
      {props.integration.canLogSentEmails && (
        <EmailLogPreference {...props} isSaving={isSaving} onSavingChange={setIsSaving} />
      )}
    </div>
  );
}

function EmailLogPreference({
  integration,
  emailLoggingIntegration,
  canUpdate,
  isSaving,
  onSavingChange
}: SettingsProps & {
  isSaving: boolean;
  onSavingChange: (saving: boolean) => void;
}) {
  const {
    hasChanges,
    preference,
    destinations,
    options,
    needsActivityCode,
    needsSuspenseCodes,
    update,
    save,
    error,
    saved
  } = useEmailLogPreference({ integration, canUpdate, onSavingChange });
  const otherConnectionLogs =
    emailLoggingIntegration && emailLoggingIntegration.id !== integration.id;
  const disabled = !canUpdate || isSaving || !!otherConnectionLogs;
  const optionsUnavailable = options.loading || !!options.error;
  const needsOptions = needsActivityCode || needsSuspenseCodes;
  const labelOfDestination = (destination: EmailAmsLogDestination) =>
    integration.type === IntegrationType.AmsNowcerts &&
    destination === EmailAmsLogDestination.Attachment
      ? 'Account attachment'
      : destinationLabels[destination];
  const codeField = (
    label: string,
    field: 'activityCode' | 'companyCode' | 'assignToEmployeeCode' | 'assignedByEmployeeCode',
    choices: { code: string; label: string }[]
  ) => (
    <Field>
      <Label>{label}</Label>
      {canUpdate ? (
        <Select
          value={preference[field] ?? ''}
          disabled={disabled || optionsUnavailable}
          onChange={(event) => update({ ...preference, [field]: event.target.value })}
        >
          <option value="">Choose {label.toLowerCase()}</option>
          {choices.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label} ({option.code})
            </option>
          ))}
        </Select>
      ) : (
        <Description>{preference[field] || 'Not configured'}</Description>
      )}
    </Field>
  );
  return (
    <Fieldset className="space-y-3">
      <Field>
        <Label>Save sent emails as</Label>
        {integration.type === IntegrationType.AmsHawksoft ? (
          <Description>Note with email attached</Description>
        ) : canUpdate ? (
          <Select
            value={preference.destination}
            disabled={disabled}
            onChange={(event) =>
              update({ destination: event.target.value as EmailAmsLogDestination })
            }
          >
            {destinations.map((destination) => (
              <option key={destination} value={destination}>
                {labelOfDestination(destination)}
              </option>
            ))}
          </Select>
        ) : (
          <Description>{labelOfDestination(preference.destination)}</Description>
        )}
      </Field>
      {needsActivityCode &&
        codeField(
          'Activity code',
          'activityCode',
          options.data?.emailAmsLogOptions.activityCodes ?? []
        )}
      {needsSuspenseCodes && (
        <>
          {codeField('Company', 'companyCode', options.data?.emailAmsLogOptions.companies ?? [])}
          {codeField(
            'Assign to',
            'assignToEmployeeCode',
            options.data?.emailAmsLogOptions.employees ?? []
          )}
          {codeField(
            'Assigned by',
            'assignedByEmployeeCode',
            options.data?.emailAmsLogOptions.employees ?? []
          )}
        </>
      )}
      <Field className="space-y-3">
        {needsSuspenseCodes && (
          <Description>
            Suspense stores the email details without an attachment. Choose a policy on the email
            before logging.
          </Description>
        )}
        {needsActivityCode && (
          <Description>
            Choose a policy on the email before logging. The email remains attached.
          </Description>
        )}
        {!integration.logsSentEmails && (
          <Description>
            Logging is off. Your preference is saved for when you turn it on.
          </Description>
        )}
        {options.loading && (
          <Description role="status">Loading email destination choices…</Description>
        )}
        {options.error && (
          <ErrorMessage role="alert">
            Couldn’t load email destination choices.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => void options.refetch().catch(() => undefined)}
            >
              Try again
            </button>
          </ErrorMessage>
        )}
        {canUpdate && integration.type !== IntegrationType.AmsHawksoft && (
          <Button
            color="white"
            disabled={disabled || !hasChanges || (needsOptions && optionsUnavailable)}
            onClick={() => void save()}
          >
            {isSaving ? 'Saving…' : saved ? 'Saved' : 'Save preference'}
          </Button>
        )}
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      </Field>
    </Fieldset>
  );
}
